<script lang="ts" setup>
/**
 * ConfirmAddressEditModal
 *
 * Wraps ConfirmAddressEdit in the standard confirm-flow modal shell. The form is
 * only mounted while the modal is open, so each open starts from `address` and a
 * cancelled edit leaves nothing behind. Save validates first and reads the value
 * straight off the form, so no keystroke can be stranded in a stale copy.
 *
 * The card is taller than the other confirm-flow modals, so it opts into top
 * alignment to keep the footer reachable on short screens.
 */
import { computed, ref } from 'vue';
import type { Address } from '@Types/WiringInstrucions.interface';
import ConfirmModal from '@Components/modals/shared/ConfirmModal.vue';
import type { ConfirmFooterButton } from '@Components/modals/shared/ConfirmModal.vue';
import ConfirmAddressEdit from '@Components/confirm/ConfirmAddressEdit.vue';

const props = defineProps<{
    modelValue: boolean;
    address: Address;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'save', value: Address): void;
}>();

const addressEdit = ref<InstanceType<typeof ConfirmAddressEdit> | null>(null);

const isModalOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value),
});

const closeModal = () => {
    isModalOpen.value = false;
};

const save = async () => {
    const form = addressEdit.value;
    if (!form || !(await form.validate())) return;
    emit('save', form.getAddress());
    isModalOpen.value = false;
};

const footerButtons = computed<ConfirmFooterButton[]>(() => [
    {
        type: 'outline',
        text: 'Cancel',
        dataId: 'confirm-address-edit-cancel',
        onClick: closeModal,
    },
    {
        type: 'primary',
        text: 'Save',
        dataId: 'confirm-address-edit-save',
        onClick: save,
    },
]);
</script>

<template>
    <confirm-modal
        v-model="isModalOpen"
        icon="cd-ic-edit-square"
        icon-style="default"
        header="Edit address"
        align-top
        align-content-left
        :footer-buttons="footerButtons"
    >
        <template #form-elements>
            <confirm-address-edit
                v-if="isModalOpen"
                ref="addressEdit"
                :address="props.address"
            />
        </template>
    </confirm-modal>
</template>
